/**
 * Loading progress indicator.
 *
 * Direct-DOM (no Store, no framework) so it can report progress from the very
 * first script parse through auth, vault load and document open — including
 * phases where the render pipeline is deliberately suspended
 * (isDocumentLoading suppresses both shells' renders).
 *
 * Two modes:
 *  - splash: full-screen panel with app name — used for the initial app boot.
 *    index.html ships a static #boot-progress element that is visible while
 *    the JS bundle is still downloading/parsing; adoptBootSplash() takes it
 *    over once the bundle runs.
 *  - bar: slim strip pinned to the top of the viewport — used when opening /
 *    switching documents inside a running app, so context stays visible.
 *
 * Progress is monotonic while a session is active: a stage can only move the
 * bar forward, so overlapping reporters (boot flow + document loader) compose
 * safely. Stages are reported at real milestone boundaries (session restore,
 * DEK retrieval, IndexedDB load, server sync, tree build), so the bar reflects
 * genuine progress rather than a timer.
 */

let el = null; // overlay root
let fillEl = null;
let labelEl = null;
let mode = null; // 'splash' | 'bar' | null (idle)
let value = 0; // 0..1 committed progress

function buildOverlay(asSplash) {
  const root = document.createElement("div");
  root.id = "load-progress";
  root.className = asSplash ? "load-progress splash" : "load-progress bar";
  root.setAttribute("role", "progressbar");
  root.setAttribute("aria-valuemin", "0");
  root.setAttribute("aria-valuemax", "100");
  root.innerHTML = `
    <div class="load-progress-inner">
      <div class="load-progress-label"></div>
      <div class="load-progress-track"><div class="load-progress-fill"></div></div>
    </div>
  `;
  document.body.appendChild(root);
  return root;
}

function bindParts() {
  fillEl = el.querySelector(".load-progress-fill");
  labelEl = el.querySelector(".load-progress-label");
}

function applyDom(label) {
  if (!el) return;
  const pct = Math.round(value * 100);
  if (fillEl) fillEl.style.width = pct + "%";
  if (label && labelEl) labelEl.textContent = label;
  el.setAttribute("aria-valuenow", String(pct));
}

/**
 * Adopt the static #boot-progress element from index.html as the active
 * splash session. Call once, as early as possible in the boot path.
 */
export function adoptBootSplash() {
  if (mode) return;
  const boot = document.getElementById("boot-progress");
  if (boot) {
    el = boot;
    // Normalize to the module's class names (the static markup already uses
    // them; this is belt-and-braces for older cached index.html).
    el.classList.add("load-progress", "splash");
  } else {
    el = buildOverlay(true);
  }
  bindParts();
  mode = "splash";
  value = Math.max(value, 0.05);
  applyDom();
}

/**
 * Start a slim top-bar progress session (document open/switch in a running
 * app). No-op if a session is already active — the doc loader then reports
 * into the existing session via beginNested()/stage().
 * @param {string} [label]
 */
export function beginBar(label) {
  if (mode) return;
  // Never reuse a previous session's element: it may be mid-fade with its own
  // removal timer pending, which would delete this session's bar from under
  // it. Drop any remnant immediately and build fresh.
  document.getElementById("load-progress")?.remove();
  el = buildOverlay(false);
  bindParts();
  mode = "bar";
  value = 0.03;
  applyDom(label);
}

/**
 * Report progress. Monotonic: a fraction that would move the bar backwards is
 * ignored (label still updates). No-op when no session is active.
 * @param {number} fraction - 0..1
 * @param {string} [label] - user-facing stage description
 */
export function stage(fraction, label) {
  if (!mode) return;
  const f = Math.min(1, Math.max(0, fraction));
  if (f > value) value = f;
  applyDom(label);
}

/**
 * Complete the active session: fill to 100%, fade out, remove.
 */
export function done() {
  if (!mode || !el) {
    mode = null;
    return;
  }
  value = 1;
  applyDom();
  const finished = el;
  mode = null;
  value = 0;
  el = null;
  fillEl = null;
  labelEl = null;
  finished.style.opacity = "0";
  // Removal timer is LOCAL to this element and never cancelled. A shared,
  // cancellable timer caused the production freeze of 2026-08-15: beginBar()
  // cleared it during the splash's fade-out, leaving an invisible fullscreen
  // overlay that swallowed every click.
  setTimeout(() => finished.remove(), 250);
}

/**
 * Complete the session only if it is a bar session. Document loaders use
 * this so a doc open that happens to run under the fullscreen BOOT splash
 * (logged-out boot, session restore) doesn't tear the splash down early —
 * the boot code owns the splash and closes it at its own end.
 */
export function doneBar() {
  if (mode === "bar") done();
}

/**
 * Whether a progress session is currently active.
 */
export function isActive() {
  return mode !== null;
}
